import { useQuery } from '@tanstack/react-query';

import { listProjects, type ProjectListItem } from '@services/projects.service';

/**
 * Fetches all projects for the current organisation.
 *
 * Cache configuration:
 *   - `staleTime: 30s` — most UI surfaces tolerate this.
 *   - `refetchOnWindowFocus: true` — covers desktop tab switches.
 *   - `refetchOnReconnect: true` — covers the user resuming from sleep
 *     or losing/regaining network without an explicit focus event.
 *   - `refetchInterval: 5min` — last-resort safety net so that mobile
 *     PWA tabs (where focus events fire unreliably) don't show a stale
 *     project list for hours. Five minutes is well above the polling
 *     cadence we care about for billing state, well below "user
 *     notices the list is wrong".
 */
export function useProjects(): {
  projects: ProjectListItem[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['projects', 'list'],
    queryFn: listProjects,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 5 * 60_000,
  });

  return {
    projects: data?.items ?? [],
    isLoading,
    error: error as Error | null,
    refetch,
  };
}
